import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../context';
import type { LoveStatus } from '../types';

export function useLoveStatus(mediaId: string | undefined) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['love', mediaId],
    queryFn: () => apiClient.get<LoveStatus>(`/api/media/${mediaId}/love`),
    enabled: !!mediaId,
  });
}

export function useToggleLove(mediaId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const loveMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/media/${mediaId}/love`, {}),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['love', mediaId] });
      const prev = queryClient.getQueryData(['love', mediaId]);
      queryClient.setQueryData(['love', mediaId], (old: LoveStatus | undefined) => {
        if (!old) return { loveCount: 1, userLoved: true, lovedBy: [] };
        return { ...old, loveCount: old.loveCount + 1, userLoved: true };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => queryClient.setQueryData(['love', mediaId], ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['love', mediaId] });
      if (mediaId) queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  const unloveMutation = useMutation({
    mutationFn: () => apiClient.delete(`/api/media/${mediaId}/love`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['love', mediaId] });
      const prev = queryClient.getQueryData(['love', mediaId]);
      queryClient.setQueryData(['love', mediaId], (old: LoveStatus | undefined) => {
        if (!old) return { loveCount: 0, userLoved: false, lovedBy: [] };
        return { ...old, loveCount: Math.max(0, old.loveCount - 1), userLoved: false };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => queryClient.setQueryData(['love', mediaId], ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['love', mediaId] });
      if (mediaId) queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  function toggle(currentlyLoved: boolean) {
    if (currentlyLoved) {
      unloveMutation.mutate();
    } else {
      loveMutation.mutate();
    }
  }

  return { toggle, isLoving: loveMutation.isPending, isUnloving: unloveMutation.isPending };
}
